import React from "react";
import {
  Button,
  CircularProgress,
  useMediaQuery,
  SxProps,
  Theme,
} from "@mui/material";
import { useSharedColors } from "../../index";
import {
  ButtonDeleteIcon,
  ButtonSearchIcon,
  ButtonClearIcon,
} from "@ippon/shared-assets";
import "./CustomButton.css";

export interface CustomButtonProps {
  label: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  startIcon?: React.ReactNode;
  sx?: SxProps<Theme>;
  buttonCategory?:
    | "submit"
    | "delete"
    | "deleteModal"
    | "cancel"
    | "cancelModal"
    | "search"
    | "clear"
    | "clearPrimary"
    | "passwordReset"
    | "confirm";
  type?: "button" | "submit" | "reset";
  isFileUpload?: boolean;
  disabled?: boolean;
  onFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  isLoading?: boolean;
  endIcon?: React.ReactNode;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  color?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | "inherit";
  spIcon?: boolean;
  useMdIcon?: boolean;
  clearIconAlwaysVisible?: boolean;
  clearIconHideBetween600And900?: boolean;
  hideIcon?: boolean;
}

const baseStyled = (colors: ReturnType<typeof useSharedColors>) => ({
  clearButton: {
    display: "flex !important",
    fontSize: "14px !important",
    height: { xs: "32px !important", sm: "34px !important" },
    minWidth: "auto !important",
    borderRadius: "10px !important",
    padding: "0 16px !important",
    backgroundColor: "#ffffff !important",
    border: "1px solid",
    borderColor: colors.border.medium,
    color: colors.text.secondary,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
      borderColor: colors.border.medium,
      backgroundColor: "#F5F5F5 !important",
    },
    "&:active": {
      boxShadow: "none",
      borderColor: colors.border.medium,
      backgroundColor: "#eeeeee !important",
    },
    "&.Mui-disabled": {
      backgroundColor: `${colors.background.disabled} !important`,
      borderColor: `${colors.border.light} !important`,
      color: `${colors.text.disabled} !important`,
    },
  },
  clearPrimaryButton: {
    fontSize: "12px",
    height: { xs: "32px", sm: "34px" },
    borderRadius: "10px",
    minWidth: "auto",
    padding: "0 16px",
    borderColor: colors.border.medium,
    color: colors.primary.main,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
      borderColor: colors.border.medium,
      backgroundColor: "transparent",
    },
    "&:active": {
      boxShadow: "none",
      borderColor: colors.border.medium,
    },
    "&.Mui-disabled": {
      backgroundColor: `${colors.background.disabled} !important`,
      borderColor: `${colors.border.light} !important`,
      color: `${colors.text.disabled} !important`,
    },
  },
  deleteButton: {
    display: "flex !important",
    height: { xs: "32px !important", sm: "34px !important" },
    padding: "0 16px !important",
    justifyContent: "center !important",
    alignItems: "center !important",
    gap: "4px !important",
    borderRadius: "10px !important",
    backgroundColor: "#dc3545 !important",
    color: "#ffffff !important",
    fontSize: "14px !important",
    fontWeight: "600 !important",
    lineHeight: "16.8px !important",
    textTransform: "none !important",
    boxShadow: "none !important",
    minWidth: "auto !important",
    fontFamily: '"Inter", "Noto Sans JP", sans-serif !important',
    "&:hover": {
      backgroundColor: "#dc3545 !important",
      boxShadow: "none !important",
    },
    "&:active": {
      backgroundColor: "#dc3545 !important",
      boxShadow: "none !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#989898 !important",
      color: "#ffffff !important",
    },
  },
  searchButton: {
    display: "flex !important",
    // height: "38px !important",
    height: { xs: "32px !important", sm: "34px !important" },
    padding: "0 16px !important",
    justifyContent: "center !important",
    alignItems: "center !important",
    gap: "0px !important",
    borderRadius: "10px !important",
    backgroundColor: "#0B9DBD !important",
    color: "white !important",
    fontSize: "14px !important",
    fontWeight: "500 !important",
    lineHeight: "16.8px !important",
    textTransform: "none !important",
    boxShadow: "none !important",
    minWidth: "auto !important",
    fontFamily: '"Inter", "Noto Sans JP", sans-serif !important',
    "& .MuiButton-startIcon": {
      marginRight: "3px !important",
      marginLeft: "0px !important",
    },
    "&:hover": {
      backgroundColor: "#0B9DBD !important",
      boxShadow: "none !important",
    },
    "&:active": {
      backgroundColor: "#0B9DBD !important",
      boxShadow: "none !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#cccccc !important",
      color: "#ffffff !important",
    },
  },
  passwordResetButton: {
    display: "flex !important",
    height: "32px !important",
    padding: "0 16px !important",
    justifyContent: "center !important",
    alignItems: "center !important",
    gap: "4px !important",
    borderRadius: "10px !important",
    backgroundColor: "#fff !important",
    color: "#0b9dbd !important",
    border: "1px solid #0b9dbd !important",
    fontSize: "12px !important",
    fontWeight: "600 !important",
    textTransform: "none !important",
    boxShadow: "inherit !important",
    minWidth: "auto !important",
    fontFamily: '"Inter", "Noto Sans JP", sans-serif !important',
    "&:hover": {
      backgroundColor: "#fff !important",
      borderColor: "#0b9dbd !important",
      boxShadow: "inherit !important",
    },
    "&:active": {
      backgroundColor: "#fff !important",
      borderColor: "#0b9dbd !important",
      boxShadow: "inherit !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f0f0f0 !important",
      color: "#999999 !important",
      borderColor: "#cccccc !important",
    },
    "@media (max-width: 600px)": {
      height: "23px !important",
      borderRadius: "10px !important",
      fontSize: "12px !important",
    },
  },
  deleteModalButton: {
    display: "flex !important",
    height: "36px !important",
    padding: "0 16px !important",
    justifyContent: "center !important",
    alignItems: "center !important",
    gap: "4px !important",
    borderRadius: "10px !important",
    backgroundColor: `${colors.status.errorBackground} !important`,
    color: `${colors.status.error} !important`,
    border: `1px solid ${colors.status.error} !important`,
    fontSize: "14px !important",
    fontWeight: "700 !important",
    lineHeight: "1.7 !important",
    textTransform: "none !important",
    boxShadow: "none !important",
    minWidth: "auto !important",
    width: "50% !important",
    fontFamily: '"Noto Sans JP", sans-serif !important',
    "&:hover": {
      backgroundColor: `${colors.status.errorBackground} !important`,
      borderColor: `${colors.status.error} !important`,
      boxShadow: "none !important",
    },
    "&:active": {
      backgroundColor: `${colors.status.errorBackground} !important`,
      borderColor: `${colors.status.error} !important`,
      boxShadow: "none !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#cccccc !important",
      color: "#ffffff !important",
      borderColor: "#cccccc !important",
    },
  },
  cancelModalButton: {
    display: "flex !important",
    height: "36px !important",
    padding: "0 16px !important",
    justifyContent: "center !important",
    alignItems: "center !important",
    gap: "4px !important",
    borderRadius: "10px !important",
    backgroundColor: "#989898 !important",
    color: "white !important",
    fontSize: "14px !important",
    fontWeight: "700 !important",
    lineHeight: "1.7 !important",
    textTransform: "none !important",
    boxShadow: "none !important",
    minWidth: "auto !important",
    width: "50% !important",
    fontFamily: '"Noto Sans JP", sans-serif !important',
    "&:hover": {
      backgroundColor: "#989898 !important",
      boxShadow: "none !important",
    },
    "&:active": {
      backgroundColor: "#989898 !important",
      boxShadow: "none !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#cccccc !important",
      color: "#ffffff !important",
    },
  },
  confirmButton: {
    display: "flex !important",
    height: "36px !important",
    padding: "0 16px !important",
    justifyContent: "center !important",
    alignItems: "center !important",
    gap: "4px !important",
    borderRadius: "10px !important",
    backgroundColor: `${colors.status.confirmBackground} !important`,
    color: `${colors.status.confirm} !important`,
    border: `1px solid ${colors.status.confirm} !important`,
    fontSize: "14px !important",
    fontWeight: "700 !important",
    lineHeight: "1.7 !important",
    textTransform: "none !important",
    boxShadow: "none !important",
    minWidth: "auto !important",
    fontFamily: '"Noto Sans JP", sans-serif !important',
    "&:hover": {
      backgroundColor: `${colors.status.confirmBackground} !important`,
      borderColor: `${colors.status.confirm} !important`,
      boxShadow: "none !important",
    },
    "&:active": {
      backgroundColor: `${colors.status.confirmBackground} !important`,
      borderColor: `${colors.status.confirm} !important`,
      boxShadow: "none !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#cccccc !important",
      color: "#ffffff !important",
      borderColor: "#cccccc !important",
    },
  },
});

const VisuallyHiddenInput = (
  props: React.InputHTMLAttributes<HTMLInputElement>
) => (
  <input
    {...props}
    style={{
      position: "absolute",
      width: "1px",
      height: "1px",
      margin: "-1px",
      padding: "0",
      border: "0",
      clip: "rect(0 0 0 0)",
      clipPath: "inset(100%)",
      overflow: "hidden",
    }}
  />
);

const CustomButton: React.FC<CustomButtonProps> = ({
  label,
  onClick,
  startIcon,
  endIcon,
  sx = {},
  buttonCategory = "submit",
  type = "button",
  isFileUpload = false,
  disabled = false,
  onFileChange,
  className = "",
  isLoading = false,
  variant,
  size = "medium",
  fullWidth = false,
  color = "primary",
  spIcon = false,
  useMdIcon = false,
  clearIconAlwaysVisible = false,
  clearIconHideBetween600And900 = false,
  hideIcon = false,
}) => {
  const colors = useSharedColors();
  const styled = baseStyled(colors);
  const isClearButton = buttonCategory === "clear";
  const isClearPrimaryButton = buttonCategory === "clearPrimary";
  const isDeleteButton = buttonCategory === "delete";
  const isDeleteModalButton = buttonCategory === "deleteModal";
  const isCancelModalButton = buttonCategory === "cancelModal";
  const isSearchButton = buttonCategory === "search";
  const isPasswordResetButton =
    buttonCategory === "passwordReset" || label === "パスワードリセット";
  const isConfirmButton = buttonCategory === "confirm";
  const isMobile = useMediaQuery("(max-width:600px)");
  const isMobileUnder600 = useMediaQuery("(max-width:599px)");
  const isMd = useMediaQuery("(max-width:900px)");

  const determineVariant = () => {
    if (variant) return variant;
    return isClearButton || isClearPrimaryButton || isPasswordResetButton
      ? "outlined"
      : "contained";
  };

  const getStartIcon = () => {
    if (isLoading || hideIcon) return null;

    // クリアボタンのアイコン表示条件
    // - clearIconAlwaysVisible: 常に表示
    // - clearIconHideBetween600And900: 600px未満のみ表示（600-900pxは非表示）
    // - 上記以外: モバイル(600px未満)では常に表示、中サイズ(600-900px)ではuseMdIconがtrueの場合のみ表示、デスクトップ(901px以上)では非表示
    let showClearIcon: boolean;
    if (clearIconAlwaysVisible) {
      showClearIcon = true;
    } else if (clearIconHideBetween600And900) {
      showClearIcon = isMobileUnder600;
    } else {
      showClearIcon = isMobile || (useMdIcon && isMd);
    }
    if (isClearButton && !showClearIcon) return null;

    if (isDeleteButton && !startIcon) return <ButtonDeleteIcon />;
    if (isSearchButton && !startIcon) return <ButtonSearchIcon />;
    if (isClearButton && !startIcon) return <ButtonClearIcon />;
    return startIcon;
  };

  return (
    <Button
      variant={determineVariant()}
      disabled={disabled || isLoading}
      startIcon={getStartIcon()}
      endIcon={!isLoading ? endIcon : null}
      type={type}
      component={isFileUpload ? "label" : "button"}
      disableElevation
      size={size}
      fullWidth={fullWidth}
      color={color}
      sx={{
        whiteSpace: "nowrap",
        ...(isClearButton ? styled.clearButton : {}),
        ...(isClearPrimaryButton ? styled.clearPrimaryButton : {}),
        ...(isDeleteButton ? styled.deleteButton : {}),
        ...(isDeleteModalButton ? styled.deleteModalButton : {}),
        ...(isCancelModalButton ? styled.cancelModalButton : {}),
        ...(isSearchButton ? styled.searchButton : {}),
        ...(isPasswordResetButton ? styled.passwordResetButton : {}),
        ...(isConfirmButton ? styled.confirmButton : {}),
        textTransform: "none",
        ...sx,
        ...(isDeleteButton ||
        isDeleteModalButton ||
        isCancelModalButton ||
        isSearchButton ||
        isPasswordResetButton ||
        isConfirmButton
          ? {}
          : {
              "&.Mui-disabled": {
                backgroundColor:
                  isClearButton || isClearPrimaryButton
                    ? `${colors.background.disabled} !important`
                    : `${colors.border.light} !important`,
                borderColor:
                  isClearButton || isClearPrimaryButton
                    ? `${colors.border.light} !important`
                    : `${colors.border.light} !important`,
                color:
                  isClearButton || isClearPrimaryButton
                    ? `${colors.text.disabled} !important`
                    : "#fff",
                opacity: 1,
              },
            }),
        ...(spIcon && isMobile
          ? {
              minWidth: "40px !important",
              width: "40px",
              padding: "0 8px !important",
              "& .MuiButton-label": {
                display: "none !important",
              },
              "& .MuiButton-startIcon": {
                margin: "0 !important",
              },
            }
          : {}),
      }}
      className={
        isClearButton ||
        isClearPrimaryButton ||
        isDeleteButton ||
        isDeleteModalButton ||
        isCancelModalButton ||
        isSearchButton ||
        isPasswordResetButton ||
        isConfirmButton
          ? className
          : `buttonStyle ${className} ${isMobile ? "sp" : ""} ${
              buttonCategory === "submit"
                ? "submit"
                : buttonCategory === "cancel"
                  ? "cancel"
                  : "submit"
            }`
      }
      onClick={isLoading ? undefined : onClick}
      aria-label={spIcon && isMobile ? label : undefined}
    >
      {isLoading ? (
        <CircularProgress size={24} color="inherit" />
      ) : spIcon && isMobile ? (
        ""
      ) : (
        label
      )}
      {isFileUpload && (
        <VisuallyHiddenInput type="file" onChange={onFileChange} multiple />
      )}
    </Button>
  );
};

export default CustomButton;
